import React, { useContext, useState, useMemo } from "react";
import {
    Container,
    Table,
    Button,
    InputGroup,
    FormControl,
    Spinner,
} from "react-bootstrap";
import "./certification-of-holdings-container.scss";
import ReportsService from "../../../../../../services/reports-service";
import { IdentityContext } from "../../../../../../context/identity-context";
import UploadModal from "./components/modal/upload-modal/upload-modal";
import GenerateReportModal from "./components/modal/generate-report-modal/generate-report-modal"
import SendReportModal from "./components/modal/send-report-modal/send-report-modal";
import ReportCommentModal from "./components/modal/report-comment-modal/report-comment-modal";

const CertificationOfHoldingsContainer = ({ items }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [openRows, setOpenRows] = useState([]);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [showSendModal, setShowSendModal] = useState(false);
    const [generateMessage, setGenerateMessage] = useState("");
    const [sendMessage, setSendMessage] = useState("");
    const [selectedReport, setSelectedReport] = useState(null);
    const [checkedReportIndexes, setCheckedReportIndexes] = useState(new Set());
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [selectedComment, setSelectedComment] = useState("");
    const [isSuccess, setIsSuccess] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
    const [sendingReports, setSendingReports] = useState(false);

    const reportService = new ReportsService();
    const { user } = useContext(IdentityContext);
    const makerName = user.attributes['custom:display_name'];

    const canGenerateReports = useMemo(() => {
        return Array.from(checkedReportIndexes).every((id) => {
            const selectedItem = items.find(
                (item) => item.id.toString() === id.toString()
            );
            
            if (selectedItem) {
                return (
                    selectedItem?.status === "Not Available"
                );
            }
            return false;
        });
    }, [checkedReportIndexes, items]);  // Runs only when these dependencies change
    

    const canSendReports = useMemo(() => {
        return Array.from(checkedReportIndexes).every((id) => {
            const selectedItem = items.find(
                (item) => item.id.toString() === id.toString()
            );
            return (
                selectedItem?.status === "Report Available" || selectedItem?.status === "For Adjustment"
            );
        });
    }, [checkedReportIndexes, items]);    

    const handleRowClick = (itemId) => {
        setOpenRows((prevOpenRows) =>
            prevOpenRows.includes(itemId)
                ? prevOpenRows.filter((id) => id !== itemId)
                : [...prevOpenRows, itemId]
        );
    };

    const handleUploadClick = (report) => {
        setSelectedReport(report);
        setShowUploadModal(true);
    };

    const handleCloseModal = () => {
        setShowUploadModal(false);
        setSelectedReport(null);
    };

    const handleCheckboxChange = (itemId, isChecked) => {
        setCheckedReportIndexes((prevIndexes) => {
            const updatedIndexes = new Set(prevIndexes);
            if (isChecked) {
                updatedIndexes.add(itemId);
            } else {
                updatedIndexes.delete(itemId);
            }
            return new Set(updatedIndexes); // Ensure new reference for re-render
        });
    };
    
    const handleCommentClick = (comment) => {
        setSelectedComment(comment);
        setShowCommentModal(true);
    };

    // Handle the preview action
    const handlePreviewClick = async (report) => {
        try {
            const response = await reportService.retrieveReport(
                report.reportType,
                report.bpId,
                report.reportTitle,
                report.sellingAgentRequestDate
            );
            const newWindow = window.open(
                response.fileURL,
                "_blank",
                "noopener,noreferrer"
            );
            if (newWindow) newWindow.opener = null;
        } catch (error) {
            console.error("Error fetching report preview:", error.message);
        }
    };

    const handleDownloadClick = async (report) => {
        try {
            // Retrieve the report using the reportService
            const response = await reportService.retrieveReport(
                report.reportType,
                report.bpId,
                report.reportTitle,
                report.sellingAgentRequestDate
            );

            const url = response.fileURL;
            const filename =
                response.fileName || url.substring(url.lastIndexOf("/") + 1); // Use fileName from response


            const fileResponse = await fetch(url);
            if (!fileResponse.ok)
                throw new Error("Network response was not ok");

            const blob = await fileResponse.blob();
            const objectUrl = window.URL.createObjectURL(blob);

            // To download report
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = filename; // Use the correct filename
            link.style.display = "none";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(objectUrl);
        } catch (error) {
            if (error.response) {
                console.error(
                    "Error fetching report download:",
                    error.response.data
                );
                console.error("Error status:", error.response.status);
                console.error("Error headers:", error.response.headers);
            } else {
                console.error("Error message:", error.message);
            }
        }
    };

    const handleGenerateClick = () => {
        const selectedReports = items.filter((item) =>
            checkedReportIndexes.has(item.id.toString())
        );
    
        setGenerateMessage("Generating reports...");
        setSelectedReport(selectedReports); 
        setShowGenerateModal(true);
    };

    const handleSendClick = () => {
        setSendingReports(true);
        const checkedReportsCount = checkedReportIndexes.size;
        let successCount = 0;
    
        if (checkedReportsCount > 0) {
            const sendPromises = Array.from(checkedReportIndexes).map((itemId) => {
                const selectedItem = items.find(
                    (item) => item.id.toString() === itemId
                );
    
                // Call rrMakerSubmitCertificationOfHoldings instead of sendReport
                return reportService
                    .rrMakerSubmitCertificationOfHoldings(
                        selectedItem.bpId,
                        selectedItem.sellingAgentRequestDate,
                        makerName
                    )
                    .then((response) => {
                        // console.log("Certification of Holdings Submitted: ", response);
                        successCount++;
                    })
                    .catch((error) => {
                        console.error("Error submitting Certification of Holdings: ", error);
                    });
            });
    
            Promise.all(sendPromises)
                .then(() => {
                    setSendingReports(false);
                    if (successCount === checkedReportsCount) {
                        setIsSuccess(true);
                        setSendMessage(
                            `Certification of Holdings Successfully Submitted\n${checkedReportsCount} confirmations sent.`
                        );
                    } else {
                        setIsSuccess(false);
                        setSendMessage(
                            `Some Certification of Holdings were not submitted. Successfully submitted ${successCount} out of ${checkedReportsCount}.`
                        );
                    }
                    setShowSendModal(true);
                })
                .catch(() => {
                    setSendingReports(false);
                });
        } else {
            setSendMessage(
                "No Certification of Holdings Submitted\nThere are no confirmations for the selected items."
            );
            setShowSendModal(true);
        }
    };
    

    const handleGenerateModalClose = () => {
        setShowGenerateModal(false);
        if (isSuccess) {
            window.location.reload(); // Refresh only if success
        }
    };

    const handleSendModalClose = () => {
        setShowSendModal(false);
        setSendingReports(false);
        
        // Reload the page only if the submission was successful
        if (isSuccess) {
            window.location.reload();
        }
    };
    

    // Sorting function
    const sortedItems = [...items].sort((a, b) => {
        const dateA = a.sellingAgentRequestDate ? new Date(a.sellingAgentRequestDate) : new Date(0);
        const dateB = b.sellingAgentRequestDate ? new Date(b.sellingAgentRequestDate) : new Date(0);
    
        if (isNaN(dateA) || isNaN(dateB)) {
            return 0; // Prevent sorting errors due to invalid dates
        }
    
        if (sortConfig?.direction === "asc") {
            return dateA - dateB;
        } else {
            return dateB - dateA;
        }
    });
    

    const handleSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });
    };

    const filteredItems = sortedItems.filter((item) => {
        const reportTitle = item.reportTitle ? item.reportTitle.toLowerCase() : "";
        const sellingAgentEmailAddress = item.sellingAgentEmailAddress
            ? item.sellingAgentEmailAddress.toLowerCase()
            : "";
        const bpId = item.bpId ? item.bpId.toLowerCase() : "";
        // const name = item.name ? item.name.toLowerCase() : "";
        // const isin = item.isin ? item.isin.toLowerCase() : "";
        const sellingAgentRequestDate = item.sellingAgentRequestDate
            ? item.sellingAgentRequestDate.toLowerCase()
            : "";
        const status = item.status ? item.status.toLowerCase() : "";
        const searchLower = searchTerm.toLowerCase();

        return (
            reportTitle.includes(searchLower) ||
            sellingAgentEmailAddress.includes(searchLower) ||
            bpId.includes(searchLower) ||
            sellingAgentRequestDate.includes(searchLower) ||
            status.includes(searchLower)
            // name.includes(searchLower) ||
            // isin.includes(searchLower)
        );
    });

    return (
        <Container className="report-container">
            {/* Search Bar */}
            <InputGroup className="mb-3">
                <FormControl
                    placeholder="Search..."
                    aria-label="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </InputGroup>

            {/* Content Area */}
            <div className="content-wrapper">
                <div className="content-scrollable">
                    <Table bordered>
                        <thead>
                            <tr>
                                <th></th>
                                <th>REPORT NAME</th>
                                <th>FROM</th>
                                <th>BPID</th>
                                <th
                                    onClick={() => handleSort("sellingAgentRequestDate")}
                                    style={{ cursor: "pointer" }}
                                >
                                    Date of Request{" "}
                                    {sortConfig.key === "sellingAgentRequestDate"
                                        ? sortConfig.direction === "asc"
                                            ? "▲"
                                            : "▼"
                                        : ""}
                                </th>
                                <th>STATUS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItems.map((item) => (
                                <React.Fragment key={item.id}>
                                    <tr onClick={() => handleRowClick(item.id)}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={checkedReportIndexes.has(
                                                    item.id
                                                )} // Check if the item is selected
                                                onChange={(e) =>
                                                    handleCheckboxChange(
                                                        item.id,
                                                        e.target.checked
                                                    )
                                                } // Handle checkbox change
                                                disabled={
                                                    item.status !==
                                                        "Report Available" &&
                                                    item.status !==
                                                        "Not Available" &&
                                                    item.status !==
                                                        "For Processing" &&
                                                    item.status !==
                                                        "For Adjustment"
                                                }
                                            />
                                        </td>
                                        <td>{item.reportTitle}</td>
                                        <td>{item.sellingAgentEmailAddress}</td>
                                        <td>{item.bpId}</td>
                                        <td>{item.sellingAgentRequestDate}</td>
                                        <td>
                                            {item.status}
                                            {item.comment ? (
                                                <span
                                                    style={{
                                                        cursor: "pointer",
                                                        marginLeft: "5px",
                                                    }}
                                                    onClick={() =>
                                                        handleCommentClick(
                                                            item.comment
                                                        )
                                                    }
                                                >
                                                    ▼
                                                </span>
                                            ) : (
                                                ""
                                            )}
                                        </td>
                                            <td>
                                                {/* Conditionally render the buttons based on report availability */}
                                                {item.status !== "Not Available" && (
                                                    <>
                                                        {(item.status === "Report Available" || item.status === "For Adjustment") && (
                                                            <Button
                                                                variant="primary"
                                                                className="action-button"
                                                                onClick={() => handleUploadClick(item)}
                                                            >
                                                                Upload
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="primary"
                                                            className="action-button"
                                                            onClick={() => handlePreviewClick(item)}
                                                            disabled={item.status === "Generating Report"}
                                                        >
                                                            Preview
                                                        </Button>
                                                        <Button
                                                            variant="primary"
                                                            className="action-button"
                                                            onClick={() => handleDownloadClick(item)}
                                                            disabled={item.status === "Generating Report"}
                                                        >
                                                            Download
                                                        </Button>
                                                    </>
                                                )}
                                            </td>

                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
            <Button 
                variant="primary" 
                onClick={handleGenerateClick} 
                disabled={checkedReportIndexes.size !== 1 || !canGenerateReports}
            >
                Generate Report
            </Button>

                <Button
                    variant="primary"
                    onClick={handleSendClick}
                    disabled={!canSendReports || sendingReports}
                >
                    {sendingReports ? (
                        <>
                            <Spinner
                                animation="border"
                                style={{ width: "20px", height: "20px" }}
                            ></Spinner>
                        </>
                    ) : (
                        "Send Reports"
                    )}
                </Button>
            </div>

            {/* Upload Modal */}
            {selectedReport && (
                <>
                <UploadModal
                show={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                selectedReport={selectedReport}
            />
                </>
            )}

            <GenerateReportModal
                show={showGenerateModal}
                onClose={() => setShowGenerateModal(false)} 
                reports={selectedReport} 
            />

            <SendReportModal
                show={showSendModal}
                onClose={handleSendModalClose}
                message={sendMessage}
            />

            <ReportCommentModal 
                show={showCommentModal} 
                onClose={() => setShowCommentModal(false)} 
                comment={selectedComment} 
            />
        </Container>
    );
};

export default CertificationOfHoldingsContainer;
